// 用户认证服务
import { v4 as uuidv4 } from 'uuid'
import { SecurityUtils } from '../utils/security'
import { ErrorHandler } from '../utils/errorHandler'
import AppConfig from '../utils/config'
import { SimplePermissionService } from './simplePermissionService'

export class AuthService {
  // 用户状态
  static currentUser = null
  static isAuthenticated = false
  static sessionToken = null

  // 用户数据存储键
  static STORAGE_KEYS = {
    USER: 'altverse_user',
    SESSION: 'altverse_session',
    USERS: 'altverse_users' // 存储所有用户数据
  }

  /**
   * 初始化认证服务
   */
  static init() {
    try {
      // 检查是否有保存的会话
      this.restoreSession()
      console.log('🔐 认证服务初始化完成')
    } catch (error) {
      console.error('❌ 认证服务初始化失败:', error)
      ErrorHandler.logError(error, 'AuthService.init', ErrorHandler.SEVERITY_LEVELS.HIGH)
    }
  }

  /**
   * 用户注册
   * @param {Object} userData - 用户数据
   * @returns {Promise<Object>} 注册结果
   */
  static async register(userData) {
    try {
      // 验证输入数据
      const validationResult = this.validateUserData(userData)
      if (!validationResult.isValid) {
        throw new Error(validationResult.errors.join(', '))
      }

      // 检查用户是否已存在
      if (await this.userExists(userData.email)) {
        throw new Error('用户已存在')
      }

      // 创建用户对象
      const user = {
        id: uuidv4(),
        email: userData.email.toLowerCase(),
        username: userData.username,
        password: await this.hashPassword(userData.password),
        profile: {
          displayName: userData.username, // 使用用户名作为显示名
          avatar: userData.avatar || null,
          bio: userData.bio || '',
          preferences: {
            theme: 'dark',
            language: 'zh-CN',
            notifications: true
          }
        },
        role: 'user', // 默认角色
        permissions: this.getDefaultPermissions(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: null,
        isActive: true
      }

      // 保存用户数据
      await this.saveUser(user)
      
      // 自动登录
      await this.login(userData.email, userData.password)

      console.log('✅ 用户注册成功:', user.email)
      return {
        success: true,
        user: this.sanitizeUser(user),
        message: '注册成功'
      }
    } catch (error) {
      console.error('❌ 用户注册失败:', error)
      ErrorHandler.logError(error, 'AuthService.register', ErrorHandler.SEVERITY_LEVELS.MEDIUM)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * 用户登录
   * @param {string} email - 邮箱
   * @param {string} password - 密码
   * @returns {Promise<Object>} 登录结果
   */
  static async login(email, password) {
    try {
      // 验证输入
      if (!email || !password) {
        throw new Error('邮箱和密码不能为空')
      }

      // 查找用户
      const user = await this.findUserByEmail(email.toLowerCase())
      if (!user) {
        throw new Error('用户不存在')
      }

      // 验证密码
      const isValidPassword = await this.verifyPassword(password, user.password)
      if (!isValidPassword) {
        throw new Error('密码错误')
      }

      // 检查用户状态
      if (!user.isActive) {
        throw new Error('账户已被禁用')
      }

      // 创建会话
      const session = await this.createSession(user.id)
      
      // 更新最后登录时间
      user.lastLoginAt = new Date().toISOString()
      await this.saveUser(user)

      // 设置当前用户
      this.currentUser = user
      this.isAuthenticated = true
      this.sessionToken = session.token

      console.log('✅ 用户登录成功:', user.email)
      return {
        success: true,
        user: this.sanitizeUser(user),
        session: session,
        message: '登录成功'
      }
    } catch (error) {
      console.error('❌ 用户登录失败:', error)
      ErrorHandler.logError(error, 'AuthService.login', ErrorHandler.SEVERITY_LEVELS.MEDIUM)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * 用户登出
   * @returns {Promise<Object>} 登出结果
   */
  static async logout() {
    try {
      if (this.sessionToken) {
        await this.destroySession(this.sessionToken)
      }

      // 清除当前用户状态
      this.currentUser = null
      this.isAuthenticated = false
      this.sessionToken = null

      // 清除本地存储
      localStorage.removeItem(this.STORAGE_KEYS.USER)
      localStorage.removeItem(this.STORAGE_KEYS.SESSION)

      console.log('✅ 用户登出成功')
      return {
        success: true,
        message: '登出成功'
      }
    } catch (error) {
      console.error('❌ 用户登出失败:', error)
      ErrorHandler.logError(error, 'AuthService.logout', ErrorHandler.SEVERITY_LEVELS.LOW)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * 获取当前用户
   * @returns {Object|null} 当前用户信息
   */
  static getCurrentUser() {
    return this.currentUser ? this.sanitizeUser(this.currentUser) : null
  }

  /**
   * 检查是否已登录
   * @returns {boolean} 是否已登录
   */
  static isLoggedIn() {
    return this.isAuthenticated && this.currentUser !== null
  }

  /**
   * 验证用户权限
   * @param {string} permission - 权限名称
   * @returns {boolean} 是否有权限
   */
  static hasPermission(permission) {
    if (!this.isLoggedIn()) return false
    return this.currentUser.permissions.includes(permission)
  }

  /**
   * 检查用户角色
   * @param {string} role - 角色名称
   * @returns {boolean} 是否有该角色
   */
  static hasRole(role) {
    if (!this.isLoggedIn()) return false
    return this.currentUser.role === role
  }

  /**
   * 更新用户资料
   * @param {Object} updates - 更新数据
   * @returns {Promise<Object>} 更新结果
   */
  static async updateProfile(updates) {
    try {
      if (!this.isLoggedIn()) {
        throw new Error('请先登录')
      }

      // 验证更新数据
      const validationResult = this.validateProfileUpdates(updates)
      if (!validationResult.isValid) {
        throw new Error(validationResult.errors.join(', '))
      }

      // 更新用户数据
      const updatedUser = {
        ...this.currentUser,
        ...updates,
        updatedAt: new Date().toISOString()
      }

      // 保存更新
      await this.saveUser(updatedUser)
      this.currentUser = updatedUser

      console.log('✅ 用户资料更新成功')
      return {
        success: true,
        user: this.sanitizeUser(updatedUser),
        message: '资料更新成功'
      }
    } catch (error) {
      console.error('❌ 用户资料更新失败:', error)
      ErrorHandler.logError(error, 'AuthService.updateProfile', ErrorHandler.SEVERITY_LEVELS.MEDIUM)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * 修改密码
   * @param {string} oldPassword - 旧密码
   * @param {string} newPassword - 新密码
   * @returns {Promise<Object>} 修改结果
   */
  static async changePassword(oldPassword, newPassword) {
    try {
      if (!this.isLoggedIn()) {
        throw new Error('请先登录')
      }

      // 验证旧密码
      const isValidOldPassword = await this.verifyPassword(oldPassword, this.currentUser.password)
      if (!isValidOldPassword) {
        throw new Error('旧密码错误')
      }

      // 验证新密码
      if (!this.validatePassword(newPassword)) {
        throw new Error('新密码格式不正确')
      }

      // 更新密码
      const updatedUser = {
        ...this.currentUser,
        password: await this.hashPassword(newPassword),
        updatedAt: new Date().toISOString()
      }

      await this.saveUser(updatedUser)
      this.currentUser = updatedUser

      console.log('✅ 密码修改成功')
      return {
        success: true,
        message: '密码修改成功'
      }
    } catch (error) {
      console.error('❌ 密码修改失败:', error)
      ErrorHandler.logError(error, 'AuthService.changePassword', ErrorHandler.SEVERITY_LEVELS.MEDIUM)
      return {
        success: false,
        error: error.message
      }
    }
  }

  // ==================== 私有方法 ====================

  /**
   * 验证用户数据
   * @param {Object} userData - 用户数据
   * @returns {Object} 验证结果
   */
  static validateUserData(userData) {
    const errors = []

    // 验证邮箱
    if (!userData.email || !SecurityUtils.validateInput(userData.email, 'email')) {
      errors.push('邮箱格式不正确')
    }

    // 验证用户名
    if (!userData.username || !SecurityUtils.validateInput(userData.username, 'text')) {
      errors.push('用户名不能为空')
    }

    // 验证密码
    if (!userData.password || !this.validatePassword(userData.password)) {
      errors.push('密码格式不正确')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * 验证密码格式
   * @param {string} password - 密码
   * @returns {boolean} 是否有效
   */
  static validatePassword(password) {
    // 至少8位，包含字母和数字
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/
    return passwordRegex.test(password)
  }

  /**
   * 验证资料更新数据
   * @param {Object} updates - 更新数据
   * @returns {Object} 验证结果
   */
  static validateProfileUpdates(updates) {
    const errors = []

    if (updates.email && !SecurityUtils.validateInput(updates.email, 'email')) {
      errors.push('邮箱格式不正确')
    }

    if (updates.username && !SecurityUtils.validateInput(updates.username, 'text')) {
      errors.push('用户名不能为空')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * 检查用户是否存在
   * @param {string} email - 邮箱
   * @returns {Promise<boolean>} 是否存在
   */
  static async userExists(email) {
    const users = await this.getAllUsers()
    return users.some(user => user.email === email.toLowerCase())
  }

  /**
   * 根据邮箱查找用户
   * @param {string} email - 邮箱
   * @returns {Promise<Object|null>} 用户对象
   */
  static async findUserByEmail(email) {
    const users = await this.getAllUsers()
    return users.find(user => user.email === email.toLowerCase()) || null
  }

  /**
   * 获取所有用户
   * @returns {Promise<Array>} 用户列表
   */
  static async getAllUsers() {
    try {
      const usersData = localStorage.getItem(this.STORAGE_KEYS.USERS)
      return usersData ? JSON.parse(usersData) : []
    } catch (error) {
      console.error('获取用户列表失败:', error)
      return []
    }
  }

  /**
   * 保存用户数据
   * @param {Object} user - 用户对象
   */
  static async saveUser(user) {
    try {
      const users = await this.getAllUsers()
      const existingIndex = users.findIndex(u => u.id === user.id)
      
      if (existingIndex >= 0) {
        users[existingIndex] = user
      } else {
        users.push(user)
      }

      localStorage.setItem(this.STORAGE_KEYS.USERS, JSON.stringify(users))
    } catch (error) {
      console.error('保存用户数据失败:', error)
      throw error
    }
  }

  /**
   * 创建会话
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 会话对象
   */
  static async createSession(userId) {
    const session = {
      token: uuidv4(),
      userId: userId,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7天过期
    }

    localStorage.setItem(this.STORAGE_KEYS.SESSION, JSON.stringify(session))
    return session
  }

  /**
   * 销毁会话
   * @param {string} token - 会话令牌
   */
  static async destroySession(token) {
    localStorage.removeItem(this.STORAGE_KEYS.SESSION)
  }

  /**
   * 恢复会话
   */
  static async restoreSession() {
    try {
      const sessionData = localStorage.getItem(this.STORAGE_KEYS.SESSION)
      if (!sessionData) return

      const session = JSON.parse(sessionData)
      
      // 检查会话是否过期
      if (new Date(session.expiresAt) < new Date()) {
        localStorage.removeItem(this.STORAGE_KEYS.SESSION)
        return
      }

      // 查找用户
      const user = await this.findUserByEmail(session.userId)
      if (user && user.isActive) {
        this.currentUser = user
        this.isAuthenticated = true
        this.sessionToken = session.token
        console.log('✅ 会话恢复成功:', user.email)
      }
    } catch (error) {
      console.error('恢复会话失败:', error)
      localStorage.removeItem(this.STORAGE_KEYS.SESSION)
    }
  }

  /**
   * 密码哈希
   * @param {string} password - 原始密码
   * @returns {Promise<string>} 哈希后的密码
   */
  static async hashPassword(password) {
    // 简单的哈希实现，生产环境应使用更安全的方法
    const encoder = new TextEncoder()
    const data = encoder.encode(password + 'altverse_salt')
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * 验证密码
   * @param {string} password - 原始密码
   * @param {string} hashedPassword - 哈希后的密码
   * @returns {Promise<boolean>} 是否匹配
   */
  static async verifyPassword(password, hashedPassword) {
    const hashed = await this.hashPassword(password)
    return hashed === hashedPassword
  }

  /**
   * 获取默认权限
   * @returns {Array} 权限列表
   */
  static getDefaultPermissions() {
    return SimplePermissionService.ROLE_PERMISSIONS.user
  }

  /**
   * 清理用户数据（移除敏感信息）
   * @param {Object} user - 用户对象
   * @returns {Object} 清理后的用户对象
   */
  static sanitizeUser(user) {
    const { password, ...sanitizedUser } = user
    return sanitizedUser
  }
}

// 自动初始化
AuthService.init()

export default AuthService
